import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ArrowRight, Database, FileText, CheckCircle, RotateCcw } from "lucide-react";
import axios from "axios";
import StripeCheckout from "../checkout/StripeCheckout";
import TagPreview from "../tag/TagPreview";
import TagMaterialImage from "../tag/TagMaterialImage";
import AccessoryPicker from "../tag/AccessoryPicker";
import FontDropdown from "../tag/FontDropdown";
import LineInputWithFormat from "../tag/LineInputWithFormat";
import { generateQrCodeSVG, generateQrCodeUrl, encodeQrCodeText } from "../../utils/qrCodeUtils";
import { formatPhoneNumber, validatePhoneNumber, getUnformattedPhone } from "../../utils/phoneUtils";
import {
  DEFAULT_LINE_FORMATS,
  formatKeyForLine,
  getLineFormat,
  applyLineFormat,
  collectLineFormats,
} from "../../utils/tagLineFormat";
import { saveContact, saveQrCode, createOrder, saveTag, saveShipping, getInventory } from "../../api/tagApi";
import { buildCatalogFromMaterials, buildFallbackCatalog } from "../../utils/materialCatalog";
import DarkModeToggle from "../theme/DarkModeToggle";
import { DEFAULT_FONT, AVAILABLE_FONTS } from "../../config/fonts";
import "../../assets/styles/fonts.css";

const FALLBACK_SHAPE_CATALOG = buildFallbackCatalog();
const PRICE = 11.99; // Consistent price for all silicone tags


function MaterialSelection() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get parameters from URL
  const stepFromUrl = parseInt(searchParams.get("step") || "1", 10);
  const shapeFromUrl = searchParams.get("shape");
  const materialIdFromUrl = searchParams.get("material");
  const orderTypeFromUrl = searchParams.get("type");

  const [step, setStep] = useState(stepFromUrl);
  const [selectedShape, setSelectedShape] = useState(shapeFromUrl);

  // Debug: Log component mount
  useEffect(() => {
    console.log('MaterialSelection component mounted');
    console.log('Current step:', step);
    console.log('Selected shape:', selectedShape);
  }, [step, selectedShape]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [orderType, setOrderType] = useState(orderTypeFromUrl);
  const [activeShapes, setActiveShapes] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const shapeCatalog = activeShapes ?? FALLBACK_SHAPE_CATALOG;

  // Form states
  const [qrForm, setQrForm] = useState({
    firstname: "",
    lastname: "",
    petname: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
  });

  const [engraveForm, setEngraveForm] = useState({
    petname: "",
    line1: "",
    line2: "",
    line3: "",
    name: "",
    email: "",
    phone: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    zip: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [qrCodePosition, setQrCodePosition] = useState({ x: 50, y: 50 });
  const [phoneErrors, setPhoneErrors] = useState({ qr: null, engrave: null });

  const [selectedFont, setSelectedFont] = useState(DEFAULT_FONT.id);
  const [textCase, setTextCase] = useState('title'); // 'title' or 'uppercase'
  const [designingSide, setDesigningSide] = useState(1); // Which side is currently being designed (1 or 2)
  const [side1Config, setSide1Config] = useState({
    line1: '',
    line2: '',
    line3: '',
    ...DEFAULT_LINE_FORMATS,
    fontId: DEFAULT_FONT.id,
    fontScale: 1,
  });
  const [side2Config, setSide2Config] = useState({
    line1: '',
    line2: '',
    line3: '',
    ...DEFAULT_LINE_FORMATS,
    fontId: DEFAULT_FONT.id,
    fontScale: 1,
  });
  const [qrCodeSide, setQrCodeSide] = useState(2);
  const [qrCodeText, setQrCodeText] = useState({
    line1: '',
    line2: '',
    line3: '',
    ...DEFAULT_LINE_FORMATS,
  });

  const resolvedOrderType = searchParams.get("type") || orderType;

  // Accessory selections — one per side, set during step 5
  const [side1Accessory, setSide1Accessory] = useState(null); // { id, zone }
  const [side2Accessory, setSide2Accessory] = useState(null);
  const [accessoryDesigningSide, setAccessoryDesigningSide] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    getInventory().then((data) => {
      if (!cancelled && Array.isArray(data) && data.length > 0) {
        setActiveShapes(buildCatalogFromMaterials(data));
      }
      if (!cancelled) setCatalogLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const findMaterialInCatalog = (shapeKey, materialId) => {
    const shapeData = shapeCatalog[shapeKey];
    return shapeData?.colors.find((c) => c.id === materialId) || null;
  };

  const buildSelectedMaterial = (shapeKey, colorOption) => {
    const shapeData = shapeCatalog[shapeKey];
    const shapeMap = {
      bone: 'bone',
      rectangle: 'rect',
      circle: 'circ',
      triangle: 'tri',
      hexagon: 'hex',
    };
    return {
      ...colorOption,
      shapeName: shapeData.name,
      shape: shapeMap[shapeKey] || shapeKey,
      price: PRICE,
      materialId: colorOption.materialId ?? null,
      inventoryId: colorOption.inventoryId ?? null,
    };
  };

  // Listen to URL changes (browser back/forward buttons)
  useEffect(() => {
    const urlStep = parseInt(searchParams.get("step") || "1", 10);
    const urlShape = searchParams.get("shape");
    const urlMaterialId = searchParams.get("material");
    const urlOrderType = searchParams.get("type");

    if (urlStep !== step) {
      setStep(urlStep);
      if (urlStep === 4 && urlOrderType) {
        setDesigningSide(1);
      }
    }

    // Only update selectedShape from URL if URL has a shape value
    // This prevents overriding a valid selectedShape with null
    if (urlShape && urlShape !== selectedShape) {
      setSelectedShape(urlShape);
    } else if (!urlShape && urlStep <= 1) {
      // Only clear selectedShape if we're back at step 1 and URL has no shape
      setSelectedShape(null);
    }

    // Update material from URL
    if (urlMaterialId && urlShape) {
      const material = findMaterialInCatalog(urlShape, urlMaterialId);
      if (material && material.id !== selectedMaterial?.id) {
        setSelectedMaterial(buildSelectedMaterial(urlShape, material));
      }
    } else if (selectedMaterial && urlStep <= 1) {
      setSelectedMaterial(null);
    }

    if (urlOrderType !== orderType) {
      setOrderType(urlOrderType);
    } else if (!urlOrderType && orderType && urlStep <= 3 && step <= 3) {
      setOrderType(null);
    }
  }, [searchParams, shapeCatalog]);

  const handleShapeSelect = (shapeKey) => {
    // Set state first to ensure it's available immediately
    setSelectedShape(shapeKey);
    setStep(2);
    
    // Then update URL params
    const newParams = new URLSearchParams();
    newParams.set("step", "2");
    newParams.set("shape", shapeKey);
    setSearchParams(newParams);
  };

  const handleColorSelect = (shapeKey, colorOption) => {
    setSelectedMaterial(buildSelectedMaterial(shapeKey, colorOption));
    const newParams = new URLSearchParams();
    newParams.set("step", "3");
    newParams.set("shape", shapeKey);
    newParams.set("material", colorOption.id);
    setSearchParams(newParams);
    setStep(3);
  };

  const handleOrderTypeChoose = (type) => {
    setOrderType(type);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "3");
    newParams.set("type", type);
    setSearchParams(newParams);
  };

  const handleOrderTypeContinue = () => {
    if (!resolvedOrderType) return;
    setDesigningSide(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "4");
    newParams.set("type", resolvedOrderType);
    setSearchParams(newParams);
  };

  const goToOrderTypeStep = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "3");
    setSearchParams(newParams);
  };

  const renderSelectedBranchBanner = ({ showChangeLink = true } = {}) => {
    if (!resolvedOrderType) return null;

    const isQr = resolvedOrderType === "database";
    const Icon = isQr ? Database : FileText;
    const label = isQr ? "QR Code Tag" : "Text-Only Tag";
    const description = isQr
      ? "QR on one side, engraved text on the other"
      : "Text engraved on both sides";

    return (
      <div
        data-testid="selected-order-type-banner"
        className={`mb-4 flex items-center justify-between gap-3 rounded-lg border-2 px-4 py-3 ${
          isQr
            ? "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/25"
            : "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/25"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
              isQr
                ? "bg-indigo-600 text-white dark:bg-indigo-500"
                : "bg-emerald-600 text-white dark:bg-emerald-500"
            }`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p
              className={`text-sm font-bold ${
                isQr
                  ? "text-indigo-900 dark:text-indigo-100"
                  : "text-emerald-900 dark:text-emerald-100"
              }`}
            >
              {label}
            </p>
            <p
              className={`text-xs truncate ${
                isQr
                  ? "text-indigo-700 dark:text-indigo-200"
                  : "text-emerald-700 dark:text-emerald-200"
              }`}
            >
              {description}
            </p>
          </div>
        </div>
        {showChangeLink && (
          <button
            type="button"
            onClick={goToOrderTypeStep}
            className="shrink-0 text-xs font-semibold underline underline-offset-2 text-light-textMuted dark:text-dark-textMuted hover:text-light-primary dark:hover:text-dark-primary"
          >
            Change
          </button>
        )}
      </div>
    );
  };

  const getOrderTypeCardClass = (type) => {
    const selected = resolvedOrderType === type;
    const isQr = type === "database";

    if (selected && isQr) {
      return "relative bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-600 dark:border-indigo-400 rounded-xl p-5 shadow-md ring-2 ring-indigo-500/30 ring-offset-2 dark:ring-offset-dark-surface cursor-pointer transition-all scale-[1.02]";
    }
    if (selected && !isQr) {
      return "relative bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-600 dark:border-emerald-400 rounded-xl p-5 shadow-md ring-2 ring-emerald-500/30 ring-offset-2 dark:ring-offset-dark-surface cursor-pointer transition-all scale-[1.02]";
    }
    if (isQr) {
      return "relative bg-white dark:bg-dark-surfaceHover border-2 border-light-border dark:border-dark-border rounded-xl p-5 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-card-hover dark:hover:shadow-card-hover-dark cursor-pointer transition-all";
    }
    return "relative bg-white dark:bg-dark-surfaceHover border-2 border-light-border dark:border-dark-border rounded-xl p-5 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-card-hover dark:hover:shadow-card-hover-dark cursor-pointer transition-all";
  };

  const updateSideConfig = (side, updater) => {
    if (side === 1) setSide1Config(updater);
    else setSide2Config(updater);
  };

  // Unified handler for tag text input (both sides)
  const handleTagTextChange = (side, line, value) => {
    updateSideConfig(side, (prev) => {
      const format = getLineFormat(prev, line);
      return { ...prev, [line]: applyLineFormat(value, format) };
    });
  };

  const handleTagLineFormatChange = (side, line, format) => {
    const formatKey = formatKeyForLine(line);
    updateSideConfig(side, (prev) => {
      const next = { ...prev, [formatKey]: format };
      if (prev[line]) {
        next[line] = applyLineFormat(prev[line], format);
      }
      return next;
    });
  };

  const handleQrCodeTextChange = (line, value) => {
    setQrCodeText((prev) => {
      const format = getLineFormat(prev, line);
      return { ...prev, [line]: applyLineFormat(value, format) };
    });
  };

  const handleQrCodeLineFormatChange = (line, format) => {
    const formatKey = formatKeyForLine(line);
    setQrCodeText((prev) => {
      const next = { ...prev, [formatKey]: format };
      if (prev[line]) {
        next[line] = applyLineFormat(prev[line], format);
      }
      return next;
    });
  };

  const validateFormattedLines = (config, sideLabel) => {
    for (const line of ["line1", "line2", "line3"]) {
      const value = config?.[line];
      if (!value?.trim()) continue;
      if (getLineFormat(config, line) === "phone") {
        const validation = validatePhoneNumber(value);
        if (!validation.isValid) {
          return `${sideLabel} ${line.replace("line", "Line ")}: ${validation.error}`;
        }
      }
    }
    return null;
  };

  const handleAccessoryChange = (side, accessory) => {
    if (side === 1) setSide1Accessory(accessory);
    else setSide2Accessory(accessory);
  };

  // Handle font change - applies to both sides
  const handleFontChange = (fontId) => {
    setSelectedFont(fontId);
    setSide1Config((prev) => ({ ...prev, fontId: fontId }));
    setSide2Config((prev) => ({ ...prev, fontId: fontId }));
  };

  // Flip to design the other side
  const handleFlipDesignSide = () => {
    setDesigningSide(designingSide === 1 ? 2 : 1);
  };

  const handleQrFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setQrForm((prev) => ({ ...prev, [name]: formatted }));
      const validation = validatePhoneNumber(formatted);
      setPhoneErrors((prev) => ({ ...prev, qr: validation.isValid ? null : validation.error }));
    } else {
      setQrForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEngraveChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setEngraveForm((prev) => ({ ...prev, [name]: formatted }));
      const validation = validatePhoneNumber(formatted);
      setPhoneErrors((prev) => ({ ...prev, engrave: validation.isValid ? null : validation.error }));
    } else {
      setEngraveForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Unified submit handler for tag design
  const handleSubmitTagDesign = async (e) => {
    e.preventDefault();
    setError(null);

    if (resolvedOrderType === 'database') {
      // For database orders, validate QR code text
      if (!qrCodeText.line1 && !qrCodeText.line2 && !qrCodeText.line3) {
        setError("Please enter at least one line of text to encode in the QR code");
        return;
      }

      const qrFormatError = validateFormattedLines(qrCodeText, "QR");
      if (qrFormatError) {
        setError(qrFormatError);
        return;
      }
      
      // Validate that the non-QR side has text
      const nonQrSide = qrCodeSide === 1 ? side2Config : side1Config;
      const nonQrSideHasText = nonQrSide.line1 || nonQrSide.line2 || nonQrSide.line3;
      
      if (!nonQrSideHasText) {
        setError(`Please enter at least one line of text for Side ${qrCodeSide === 1 ? 2 : 1} (the side without the QR code)`);
        return;
      }

      const side1FormatError = validateFormattedLines(side1Config, "Side 1");
      const side2FormatError = validateFormattedLines(side2Config, "Side 2");
      if (side1FormatError || side2FormatError) {
        setError(side1FormatError || side2FormatError);
        return;
      }
    } else {
      // For engrave orders, validate both sides have text
      const side1HasText = side1Config.line1 || side1Config.line2 || side1Config.line3;
      const side2HasText = side2Config.line1 || side2Config.line2 || side2Config.line3;

      if (!side1HasText) {
        setError("Please enter at least one line of text for Side 1");
        return;
      }

      if (!side2HasText) {
        setError("Please enter at least one line of text for Side 2");
        return;
      }

      const side1FormatError = validateFormattedLines(side1Config, "Side 1");
      const side2FormatError = validateFormattedLines(side2Config, "Side 2");
      if (side1FormatError || side2FormatError) {
        setError(side1FormatError || side2FormatError);
        return;
      }
    }

    // Move to accessory selection step
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "5");
    setSearchParams(newParams);
    setStep(5);
  };

  const handleSubmitQr = async (e) => {
    e.preventDefault();
    setError(null);

    if (!qrForm.firstname || !qrForm.phone || !qrForm.email) {
      setError("Please fill in all required fields (First Name, Phone, Email)");
      return;
    }

    const phoneValidation = validatePhoneNumber(qrForm.phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.error);
      setPhoneErrors((prev) => ({ ...prev, qr: phoneValidation.error }));
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "5");
    setSearchParams(newParams);
    setStep(5);
  };

  const handleSubmitEngrave = async (e) => {
    e.preventDefault();
    setError(null);

    if (!engraveForm.name || !engraveForm.email || !engraveForm.phone) {
      setError("Please fill in all required fields (Your Name, Email, Phone)");
      return;
    }

    const phoneValidation = validatePhoneNumber(engraveForm.phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.error);
      setPhoneErrors((prev) => ({ ...prev, engrave: phoneValidation.error }));
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", "5"); // accessories step
    setSearchParams(newParams);
    setStep(5);
  };

  const generateUserId = () => {
    // Use a cryptographically secure random value for the user ID suffix
    let randomPart = "";
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const array = new Uint32Array(4);
      crypto.getRandomValues(array);
      randomPart = Array.from(array, (val) => val.toString(36)).join("").slice(0, 16);
    } else {
      // Fallback: retain previous behavior if crypto is unavailable
      randomPart = Math.random().toString(36).substr(2, 16);
    }
    return `user_${Date.now()}_${randomPart}`;
  };

  const saveContactAndCreateOrder = async (contactData, hasQrCode) => {
    const saveResponse = await saveContact(contactData);
    if (!saveResponse) {
      throw new Error("Failed to save contact information");
    }

    const contactid = saveResponse?.id;
    if (!contactid) {
      throw new Error("Failed to get contact ID");
    }

    const orderid = await createOrder({
      id: contactid,
      has_qr_code: hasQrCode,
      amount: Math.round(selectedMaterial.price * 100),
      currency: "usd",
      stripe_payment_intent_id: null,  // CRITICAL: null instead of "pending"
      status: "pending",
      font_family: selectedFont,
      materialid: selectedMaterial.materialId,
      inventoryid: selectedMaterial.inventoryId,
    });

    if (!orderid) {
      throw new Error("Failed to create order");
    }

    return { contactid, orderid };
  };

  // Helper function to map tag data to tag table format
  // Uses new database schema: side_1_text_line_1, side_2_text_line_1, etc.
  const mapTagData = (orderid, side1Config, side2Config, orderType, qrCodeSide = 2) => {
    console.log('mapTagData - Input parameters:', {
      orderid,
      side1Config,
      side2Config,
      orderType,
      qrCodeSide
    });

    const isQrCodeOrder = orderType === 'database';
    
    // For database orders, skip text on the QR code side
    const side1Line1 = (isQrCodeOrder && qrCodeSide === 1) ? '' : (side1Config?.line1 || '');
    const side1Line2 = (isQrCodeOrder && qrCodeSide === 1) ? '' : (side1Config?.line2 || '');
    const side1Line3 = (isQrCodeOrder && qrCodeSide === 1) ? '' : (side1Config?.line3 || '');
    
    const side2Line1 = (isQrCodeOrder && qrCodeSide === 2) ? '' : (side2Config?.line1 || '');
    const side2Line2 = (isQrCodeOrder && qrCodeSide === 2) ? '' : (side2Config?.line2 || '');
    const side2Line3 = (isQrCodeOrder && qrCodeSide === 2) ? '' : (side2Config?.line3 || '');

    const tagData = {
      orderid: orderid,
      side_1_text_line_1: side1Line1,
      side_1_text_line_2: side1Line2,
      side_1_text_line_3: side1Line3,
      side_2_text_line_1: side2Line1,
      side_2_text_line_2: side2Line2,
      side_2_text_line_3: side2Line3,
      is_qr_code: isQrCodeOrder,
      notes: JSON.stringify({
        qr_code_side: isQrCodeOrder ? qrCodeSide : null,
        font_family: selectedFont,
        text_case: textCase,
        qr_line_1: isQrCodeOrder ? (qrCodeText.line1 || "") : "",
        qr_line_2: isQrCodeOrder ? (qrCodeText.line2 || "") : "",
        qr_line_3: isQrCodeOrder ? (qrCodeText.line3 || "") : "",
        line_formats: {
          side1: collectLineFormats(side1Config),
          side2: collectLineFormats(side2Config),
          qr: isQrCodeOrder ? collectLineFormats(qrCodeText) : null,
        },
      }),
    };

    console.log('mapTagData - Mapped tag data:', JSON.stringify(tagData, null, 2));
    return tagData;
  };

  const handleConfirmOrder = async () => {
    setIsSubmitting(true);
    setError(null);

    // Validate required checkout fields
    if (resolvedOrderType === "database") {
      if (!qrForm.firstname || !qrForm.email || !qrForm.phone || !qrForm.address_line_1) {
        setError("Please fill in all required checkout fields (First Name, Email, Phone, Street Address)");
        setIsSubmitting(false);
        return;
      }

      const phoneValidation = validatePhoneNumber(qrForm.phone);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error);
        setPhoneErrors((prev) => ({ ...prev, qr: phoneValidation.error }));
        setIsSubmitting(false);
        return;
      }

      let userId = qrForm.userid || generateUserId();
      setQrForm((prev) => ({ ...prev, userid: userId }));

      const fullname = [qrForm.firstname, qrForm.lastname].filter(Boolean).join(" ");
      const unformattedPhone = getUnformattedPhone(qrForm.phone);

      const contactData = {
        firstname: qrForm.firstname,
        lastname: qrForm.lastname || "",
        fullname: fullname,
        petname: side1Config.line1 || side2Config.line1 || "", // Extract pet name from first tag line
        phone: unformattedPhone,
        email: qrForm.email || "",
        address_line_1: qrForm.address_line_1 || "",
        address_line_2: qrForm.address_line_2 || "",
        address_line_3: qrForm.address_line_3 || "",
        city: qrForm.city || "",
        state: qrForm.state || "",
        zip: qrForm.zip || "",
      };

      try {
        const { contactid, orderid } = await saveContactAndCreateOrder(contactData, true);

        // Save tag information to tag table
        if (orderid) {
          const tagData = mapTagData(orderid, side1Config, side2Config, "database", qrCodeSide);
          console.log('handleConfirmOrder (database) - Calling saveTag with:', JSON.stringify(tagData, null, 2));
          const tagResult = await saveTag(tagData);
          if (!tagResult) {
            console.warn("Failed to save tag information, but continuing with order");
          } else {
            console.log("Tag information saved successfully:", tagResult);
            
            // Save QR code data if tag was saved successfully
            if (tagResult.id) {
              try {
                const qrCodeUrl = generateQrCodeUrl(userId);
                const qrCodeSvg = await generateQrCodeSVG(qrCodeUrl, 512, 4);
                const qrCodeData = {
                  tagid: tagResult.id,
                  qr_code_data: {
                    url: qrCodeUrl,
                    svg: qrCodeSvg,
                    text: {
                      line1: qrCodeText.line1 || "",
                      line2: qrCodeText.line2 || "",
                      line3: qrCodeText.line3 || "",
                      encoded:
                        qrCodeText.line1 || qrCodeText.line2 || qrCodeText.line3
                          ? encodeQrCodeText(
                              qrCodeText.line1 || "",
                              qrCodeText.line2 || "",
                              qrCodeText.line3 || ""
                            )
                          : null,
                    },
                  },
                };
                const qrCodeResult = await saveQrCode(qrCodeData);
                if (!qrCodeResult) {
                  console.warn("Failed to save QR code data, but continuing with order");
                } else {
                  console.log("QR code data saved successfully:", qrCodeResult);
                }
              } catch (qrError) {
                console.error("Error saving QR code:", qrError);
              }
            }
          }

          // Save shipping information to shipping table
          const shippingData = {
            orderid: orderid,
            address_line_1: qrForm.address_line_1 || "",
            address_line_2: qrForm.address_line_2 || "",
            address_line_3: qrForm.address_line_3 || "",
            city: qrForm.city || "",
            state: qrForm.state || "",
            zip: qrForm.zip || "",
            status: 'pending'
          };
          console.log('handleConfirmOrder (database) - Calling saveShipping with:', JSON.stringify(shippingData, null, 2));
          const shippingResult = await saveShipping(shippingData);
          if (!shippingResult) {
            console.warn("Failed to save shipping information, but continuing with order");
          } else {
            console.log("Shipping information saved successfully:", shippingResult);
          }
        }

        const orderData = {
          material: selectedMaterial,
          orderType: "database",
          contactInfo: {
            firstname: qrForm.firstname,
            lastname: qrForm.lastname || "",
            address_line_1: qrForm.address_line_1 || "",
            address_line_2: qrForm.address_line_2 || "",
            phone: qrForm.phone,
          },
          notificationEmail: qrForm.email,
          userid: userId,
          contactid,
          orderid,
          qrCodePosition,
          side1Config,
          side2Config,
          qrCodeSide,
          qrCodeText,
          selectedFont,
          side1Accessory,
          side2Accessory,
        };

        sessionStorage.setItem("pendingOrderData", JSON.stringify(orderData));
        setOrderData(orderData);
        setShowStripe(true);
        setIsSubmitting(false);
      } catch (error) {
        console.error("Error processing order:", error);
        setError(error.message || "Failed to process order. Please try again.");
        setIsSubmitting(false);
      }
    } else {
      // Validate required checkout fields for engrave orders
      if (!engraveForm.name || !engraveForm.email || !engraveForm.phone || !engraveForm.address_line_1) {
        setError("Please fill in all required checkout fields (Name, Email, Phone, Street Address)");
        setIsSubmitting(false);
        return;
      }

      const phoneValidation = validatePhoneNumber(engraveForm.phone);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error);
        setPhoneErrors((prev) => ({ ...prev, engrave: phoneValidation.error }));
        setIsSubmitting(false);
        return;
      }

      try {
        const firstname = engraveForm.name.split(" ")[0] || engraveForm.name;
        const lastname = engraveForm.name.split(" ").slice(1).join(" ") || "";
        const unformattedPhone = getUnformattedPhone(engraveForm.phone);

        const contactData = {
          firstname: firstname,
          lastname: lastname,
          fullname: engraveForm.name,
          petname: side1Config.line1 || side2Config.line1 || "", // Extract pet name from first tag line
          phone: unformattedPhone,
          email: engraveForm.email || "",
          address_line_1: engraveForm.address_line_1 || "",
          address_line_2: engraveForm.address_line_2 || "",
          address_line_3: engraveForm.address_line_3 || "",
          city: engraveForm.city || "",
          state: engraveForm.state || "",
          zip: engraveForm.zip || "",
        };

        console.log("contactData: ", contactData);
        const { contactid: savedContactId, orderid: savedOrderId } = await saveContactAndCreateOrder(
          contactData,
          false
        );

        // Save tag information to tag table
        if (savedOrderId) {
          const tagData = mapTagData(savedOrderId, side1Config, side2Config, "engrave", 2);
          console.log('handleConfirmOrder (engrave) - Calling saveTag with:', JSON.stringify(tagData, null, 2));
          const tagResult = await saveTag(tagData);
          if (!tagResult) {
            console.warn("Failed to save tag information, but continuing with order");
          } else {
            console.log("Tag information saved successfully:", tagResult);
          }

          // Save shipping information to shipping table
          const shippingData = {
            orderid: savedOrderId,
            address_line_1: engraveForm.address_line_1 || "",
            address_line_2: engraveForm.address_line_2 || "",
            address_line_3: engraveForm.address_line_3 || "",
            city: engraveForm.city || "",
            state: engraveForm.state || "",
            zip: engraveForm.zip || "",
            status: 'pending'
          };
          console.log('handleConfirmOrder (engrave) - Calling saveShipping with:', JSON.stringify(shippingData, null, 2));
          const shippingResult = await saveShipping(shippingData);
          if (!shippingResult) {
            console.warn("Failed to save shipping information, but continuing with order");
          } else {
            console.log("Shipping information saved successfully:", shippingResult);
          }
        }

        const orderData = {
          material: selectedMaterial,
          orderType: "engrave",
          contactInfo: {
            firstname: firstname,
            lastname: lastname,
            fullname: engraveForm.name,
            email: engraveForm.email,
            phone: engraveForm.phone,
            address_line_1: engraveForm.address_line_1 || "",
            address_line_2: engraveForm.address_line_2 || "",
          },
          notificationEmail: engraveForm.email,
          contactid: savedContactId,
          orderid: savedOrderId,
          side1Config,
          side2Config,
          selectedFont,
          side1Accessory,
          side2Accessory,
        };

        sessionStorage.setItem("pendingOrderData", JSON.stringify(orderData));
        setOrderData(orderData);
        setShowStripe(true);
        setIsSubmitting(false);
      } catch (orderError) {
        console.error("Error creating order:", orderError);
        setError("Failed to create order. Please try again.");
        setIsSubmitting(false);
      }
    }
  };

  const handleStripeSuccess = async () => {
    setSuccess(true);
    setShowStripe(false);
  };

  const handleStripeCancel = () => {
    setShowStripe(false);
    setOrderData(null);
  };

  if (success && orderData) {
    return (
      <div
        className="min-h-screen bg-gradient-light dark:bg-gradient-dark py-12 px-4 transition-colors duration-300"
        data-testid="order-complete"
      >
        <div className="max-w-lg mx-auto bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-8 text-center border border-light-border dark:border-dark-border">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-light-text dark:text-dark-text mb-2">
            Order Confirmed
          </h1>
          <p className="text-light-textMuted dark:text-dark-textMuted mb-4">
            Order #{orderData.orderid} — a confirmation email is on its way.
          </p>
          <p className="text-sm text-light-text dark:text-dark-text mb-6">
            {orderData.material?.shapeName} - {orderData.material?.name}
          </p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem("pendingOrderData");
              window.location.href = "/";
            }}
            className="px-6 py-3 bg-btn-primary dark:bg-btn-primary-dark text-white rounded-lg font-semibold"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (showStripe && orderData) {
    return (
      <StripeCheckout
        orderData={orderData}
        onSuccess={handleStripeSuccess}
        onCancel={handleStripeCancel}
      />
    );
  }


  // Safety check - ensure we have valid state
  if (typeof step !== 'number' || step < 1) {
    console.error('Invalid step value:', step);
    return (
      <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark py-6 px-4 transition-colors duration-300">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-800 dark:text-red-300">Error: Invalid step value. Please refresh the page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-light dark:bg-gradient-dark py-4 sm:py-6 transition-colors duration-300 ${step >= 4 ? 'px-2 sm:px-3' : 'px-4'}`}>
      <div className={`mx-auto ${step >= 4 ? 'max-w-7xl' : 'max-w-4xl'}`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={() => {
              if (step === 1) {
                navigate("/");
              } else {
                const newStep = step - 1;
                const newParams = new URLSearchParams(searchParams);
                newParams.set("step", newStep.toString());
                if (newStep <= 3) {
                  newParams.delete("type");
                  if (newStep === 1) {
                    newParams.delete("shape");
                    newParams.delete("material");
                  } else if (newStep === 2) {
                    newParams.delete("material");
                  }
                }
                // Going back past decorations step clears accessory state
                if (newStep < 5) {
                  setSide1Accessory(null);
                  setSide2Accessory(null);
                }
                setSearchParams(newParams);
                setStep(newStep);
              }
            }}
            className="flex items-center gap-1.5 text-light-text dark:text-dark-text hover:text-light-primary dark:hover:text-dark-primary transition-colors font-medium"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <DarkModeToggle />
        </div>

        {/* Progress Indicator */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2 text-xs">
            {/* Step 1: Shape */}
            <div className={`flex items-center gap-1 ${step >= 1 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 1 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                {step > 1 ? <CheckCircle className="w-4 h-4" /> : "1"}
              </div>
              <span className="font-semibold">Shape</span>
            </div>
            <div className="flex-1 h-1 mx-1 bg-light-border dark:bg-dark-border rounded-full">
              <div className={`h-full rounded-full transition-all ${step >= 2 ? "bg-btn-primary dark:bg-btn-primary-dark" : ""}`} style={{ width: step >= 2 ? "100%" : "0%" }} />
            </div>

            {/* Step 2: Color */}
            <div className={`flex items-center gap-1 ${step >= 2 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 2 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                {step > 2 ? <CheckCircle className="w-4 h-4" /> : "2"}
              </div>
              <span className="font-semibold">Color</span>
            </div>
            <div className="flex-1 h-1 mx-1 bg-light-border dark:bg-dark-border rounded-full">
              <div className={`h-full rounded-full transition-all ${step >= 3 ? "bg-btn-primary dark:bg-btn-primary-dark" : ""}`} style={{ width: step >= 3 ? "100%" : "0%" }} />
            </div>

            {/* Step 3: Type */}
            <div className={`flex items-center gap-1 ${step >= 3 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 3 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                {step > 3 ? <CheckCircle className="w-4 h-4" /> : "3"}
              </div>
              <span className="font-semibold">Type</span>
            </div>

            {step >= 4 && (
              <>
                <div className="flex-1 h-1 mx-1 bg-light-border dark:bg-dark-border rounded-full">
                  <div className={`h-full rounded-full transition-all ${step >= 4 ? "bg-btn-primary dark:bg-btn-primary-dark" : ""}`} style={{ width: step >= 4 ? "100%" : "0%" }} />
                </div>

                {/* Step 4: Design */}
                <div className={`flex items-center gap-1 ${step >= 4 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 4 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                    {step > 4 ? <CheckCircle className="w-4 h-4" /> : "4"}
                  </div>
                  <span className="font-semibold">Design</span>
                </div>
                <div className="flex-1 h-1 mx-1 bg-light-border dark:bg-dark-border rounded-full">
                  <div className={`h-full rounded-full transition-all ${step >= 5 ? "bg-btn-primary dark:bg-btn-primary-dark" : ""}`} style={{ width: step >= 5 ? "100%" : "0%" }} />
                </div>

                {/* Step 5: Decor */}
                <div className={`flex items-center gap-1 ${step >= 5 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 5 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                    {step > 5 ? <CheckCircle className="w-4 h-4" /> : "5"}
                  </div>
                  <span className="font-semibold">Decor</span>
                </div>
                <div className="flex-1 h-1 mx-1 bg-light-border dark:bg-dark-border rounded-full">
                  <div className={`h-full rounded-full transition-all ${step >= 6 ? "bg-btn-primary dark:bg-btn-primary-dark" : ""}`} style={{ width: step >= 6 ? "100%" : "0%" }} />
                </div>

                {/* Step 6: Review */}
                <div className={`flex items-center gap-1 ${step >= 6 ? "text-light-primary dark:text-dark-primary" : "text-light-textMuted dark:text-dark-textMuted"}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= 6 ? "bg-btn-primary dark:bg-btn-primary-dark text-white shadow-md" : "bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted border border-light-border dark:border-dark-border"}`}>
                    6
                  </div>
                  <span className="font-semibold">Review</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Step 1: Shape Selection */}
        {step === 1 && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border">
            <h1 className="text-2xl md:text-3xl font-bold mb-3 text-light-text dark:text-dark-text">
              Choose Your Tag Shape
            </h1>
            <p className="text-light-textMuted dark:text-dark-textMuted mb-6">
              Select the shape that best suits your pet's style
            </p>
            {catalogLoading && (
              <p className="text-sm text-light-textMuted dark:text-dark-textMuted mb-4">
                Loading live inventory...
              </p>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(shapeCatalog)
                .filter(([, shapeData]) => shapeData.colors.length > 0)
                .map(([shapeKey, shapeData]) => (
                <div
                  key={shapeKey}
                  data-testid={`shape-${shapeKey}`}
                  onClick={() => handleShapeSelect(shapeKey)}
                  className="bg-white dark:bg-dark-surfaceHover border-2 border-light-border dark:border-dark-border rounded-xl p-5 hover:border-light-primary dark:hover:border-dark-primary hover:shadow-card-hover dark:hover:shadow-card-hover-dark cursor-pointer transition-all group"
                >
                  <div className="w-full h-32 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                    <TagMaterialImage
                      src={shapeData.defaultImage || shapeData.colors[0]?.image}
                      alt={shapeData.name}
                      className="max-h-32"
                    />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-light-text dark:text-dark-text text-center">
                    {shapeData.name}
                  </h3>
                  <p className="text-light-textMuted dark:text-dark-textMuted text-sm text-center mb-3">
                    {shapeData.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-light-textMuted dark:text-dark-textMuted">
                      {shapeData.colors.length} {shapeData.colors.length === 1 ? 'color' : 'colors'}
                    </span>
                    <ArrowRight className="w-5 h-5 text-light-textMuted group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Color Selection */}
        {step === 2 && selectedShape && shapeCatalog[selectedShape] && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border">
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-2 text-light-text dark:text-dark-text">
                Choose Your {shapeCatalog[selectedShape]?.name || selectedShape} Color
              </h1>
              <p className="text-light-textMuted dark:text-dark-textMuted">
                {shapeCatalog[selectedShape]?.description || 'Select a color for your tag'}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {shapeCatalog[selectedShape]?.colors?.map((colorOption) => (
                <div
                  key={colorOption.id}
                  data-testid={`color-${colorOption.id}`}
                  onClick={() => handleColorSelect(selectedShape, colorOption)}
                  className="bg-white dark:bg-dark-surfaceHover border-2 border-light-border dark:border-dark-border rounded-xl p-3 hover:border-light-primary dark:hover:border-dark-primary hover:shadow-card-hover dark:hover:shadow-card-hover-dark cursor-pointer transition-all group"
                >
                  <div className="w-full h-24 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                    <TagMaterialImage
                      src={colorOption.image}
                      alt={colorOption.name}
                      className="max-h-24"
                    />
                  </div>
                  <h3 className="text-sm font-bold text-center text-light-text dark:text-dark-text">
                    {colorOption.name}
                  </h3>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-light-border dark:border-dark-border">
              <div className="flex items-center justify-between">
                <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Price:</span>
                <span className="text-2xl font-bold text-light-primary dark:text-dark-primary">
                  ${PRICE}
                </span>
              </div>
            </div>
          </div>
        )}


        {/* Step 3: Order Type Selection */}
        {step === 3 && selectedMaterial && (
          <div
            className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border"
            data-testid="order-type-step"
          >
            <div className="mb-4 flex items-center gap-3 pb-3 border-b border-light-border dark:border-dark-border">
              <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                <TagMaterialImage
                  src={selectedMaterial.image}
                  alt={`${selectedMaterial.shapeName} ${selectedMaterial.name}`}
                  className="max-h-14"
                />
              </div>
              <div>
                <h3 className="text-base font-bold text-light-text dark:text-dark-text">
                  {selectedMaterial.shapeName} - {selectedMaterial.name}
                </h3>
                <p className="text-light-primary dark:text-dark-primary text-sm font-semibold">
                  ${selectedMaterial.price}
                </p>
              </div>
            </div>

            <h2 className="text-xl font-bold mb-2 text-light-text dark:text-dark-text">
              How would you like to proceed?
            </h2>
            <p className="text-light-textMuted dark:text-dark-textMuted mb-2">
              Choose how we should handle your contact information
            </p>
            <p className="text-xs text-light-textMuted dark:text-dark-textMuted mb-6">
              Select a tag type below, then continue to design.
            </p>

            <div className="grid md:grid-cols-2 gap-3" role="radiogroup" aria-label="Tag type">
              <div
                data-testid="order-type-database"
                role="radio"
                aria-checked={resolvedOrderType === "database"}
                tabIndex={0}
                onClick={() => handleOrderTypeChoose("database")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOrderTypeChoose("database");
                  }
                }}
                className={getOrderTypeCardClass("database")}
              >
                {resolvedOrderType === "database" && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white text-xs font-bold px-2.5 py-1 dark:bg-indigo-500">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Selected
                  </span>
                )}
                <Database className="w-8 h-8 text-light-primary dark:text-dark-primary mb-3" />
                <h3 className="text-base font-bold mb-2 text-light-text dark:text-dark-text">QR Code Tag</h3>
                <p className="text-light-textMuted dark:text-dark-textMuted mb-3 text-sm">
                  QR code engraved on tag. Scannable with any smartphone to display your pet's info.
                </p>
                <ul className="text-xs text-light-textMuted dark:text-dark-textMuted space-y-1.5">
                  <li className="flex items-center gap-1">
                    <span className="text-light-primary dark:text-dark-primary font-bold">✓</span>
                    QR code engraved
                  </li>
                  <li className="flex items-center gap-1">
                    <span className="text-light-primary dark:text-dark-primary font-bold">✓</span>
                    Update info anytime
                  </li>
                </ul>
              </div>

              <div
                data-testid="order-type-engrave"
                role="radio"
                aria-checked={resolvedOrderType === "engrave"}
                tabIndex={0}
                onClick={() => handleOrderTypeChoose("engrave")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOrderTypeChoose("engrave");
                  }
                }}
                className={getOrderTypeCardClass("engrave")}
              >
                {resolvedOrderType === "engrave" && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 dark:bg-emerald-500">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Selected
                  </span>
                )}
                <FileText className="w-8 h-8 text-light-secondary dark:text-dark-secondary mb-3" />
                <h3 className="text-base font-bold mb-2 text-light-text dark:text-dark-text">Text-Only Tag</h3>
                <p className="text-light-textMuted dark:text-dark-textMuted mb-3 text-sm">
                  Directly engrave your pet's information as text. Simple and traditional.
                </p>
                <ul className="text-xs text-light-textMuted dark:text-dark-textMuted space-y-1.5">
                  <li className="flex items-center gap-1">
                    <span className="text-light-secondary dark:text-dark-secondary font-bold">✓</span>
                    Up to 3 lines of text
                  </li>
                  <li className="flex items-center gap-1">
                    <span className="text-light-secondary dark:text-dark-secondary font-bold">✓</span>
                    Traditional engraving
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="order-type-continue"
                onClick={handleOrderTypeContinue}
                disabled={!resolvedOrderType}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm bg-btn-primary dark:bg-btn-primary-dark text-white rounded-lg font-bold hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Continue to Design
                <ArrowRight className="w-4 h-4" />
              </button>
              {!resolvedOrderType && (
                <p className="text-xs text-light-textMuted dark:text-dark-textMuted sm:self-center">
                  Pick QR Code or Text-Only to continue
                </p>
              )}
            </div>
          </div>
        )}


        {step === 4 && selectedMaterial && !resolvedOrderType && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border">
            <p className="text-center text-light-textMuted dark:text-dark-textMuted">
              Loading design options…
            </p>
          </div>
        )}

        {/* Step 4: Form Entry */}
        {step === 4 && selectedMaterial && resolvedOrderType && (
          <div
            className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-4 sm:p-5 border-2 border-light-border dark:border-dark-border"
            data-testid="design-step"
          >
            <div className="mb-4 flex items-center gap-3 pb-3 border-b border-light-border dark:border-dark-border">
              <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                <TagMaterialImage
                  src={selectedMaterial.image}
                  alt={`${selectedMaterial.shapeName} ${selectedMaterial.name}`}
                  className="max-h-14"
                />
              </div>
              <div>
                <h3 className="text-base font-bold text-light-text dark:text-dark-text">
                  {selectedMaterial.shapeName} - {selectedMaterial.name}
                </h3>
                <p className="text-light-primary dark:text-dark-primary text-sm font-semibold">
                  ${selectedMaterial.price}
                </p>
              </div>
            </div>

            {renderSelectedBranchBanner()}

            <h2 className="text-xl font-bold mb-2 text-light-text dark:text-dark-text">
              Design Your Tag
            </h2>
            <p className="text-light-textMuted dark:text-dark-textMuted mb-6 text-sm">
              {resolvedOrderType === 'database' 
                ? 'Enter the text to encode in the QR code (up to 3 lines), choose which side will have the QR code, and add text for the other side.'
                : 'Enter up to 3 lines of text for each side. Start with Side 1, then flip to design Side 2.'}
            </p>

            {/* Unified Tag Design Form */}
            <form key={`design-${resolvedOrderType}`} onSubmit={handleSubmitTagDesign} className="space-y-4">
              <div className="grid lg:grid-cols-[minmax(0,35fr)_minmax(300px,65fr)] gap-4 lg:gap-5 items-stretch">
                {/* Tag preview — stretches to match the form column's real height (whatever that is), with a small floor so it never gets too squished */}
                <div className="lg:sticky lg:top-4 order-1 min-w-0 flex flex-col min-h-[280px] rounded-xl border border-light-border dark:border-dark-border bg-light-surface/40 dark:bg-dark-surfaceLight/25 p-3 sm:p-4">
                  <TagPreview
                    embedded
                    fillContainer
                    previewScale={1.4}
                    className="flex-1"
                    material={selectedMaterial}
                    orderType={resolvedOrderType}
                    formData={resolvedOrderType === "database" ? qrForm : engraveForm}
                    side1Config={side1Config}
                    side2Config={side2Config}
                    qrCodeSide={qrCodeSide}
                    qrCodeText={qrCodeText}
                    selectedFont={selectedFont}
                    textCase={textCase}
                    onTextCaseChange={setTextCase}
                    currentSide={designingSide}
                  />
                </div>

                {/* Design controls */}
                <div className="space-y-4 order-2 min-w-0">
                  {/* QR Code Side Selection - only for database orders */}
                  {resolvedOrderType === 'database' && (
                    <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-3">
                        Choose QR Code Side
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setQrCodeSide(1)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            qrCodeSide === 1
                              ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-100 dark:bg-indigo-900/40'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-surfaceLight hover:border-indigo-400 dark:hover:border-indigo-600'
                          }`}
                        >
                          <div className="text-sm font-medium text-light-text dark:text-dark-text mb-1">
                            Side 1
                          </div>
                          <div className="text-xs text-light-textMuted dark:text-dark-textMuted">
                            QR code on front
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setQrCodeSide(2)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            qrCodeSide === 2
                              ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-100 dark:bg-indigo-900/40'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-surfaceLight hover:border-indigo-400 dark:hover:border-indigo-600'
                          }`}
                        >
                          <div className="text-sm font-medium text-light-text dark:text-dark-text mb-1">
                            Side 2
                          </div>
                          <div className="text-xs text-light-textMuted dark:text-dark-textMuted">
                            QR code on back
                          </div>
                        </button>
                      </div>
                      <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-2">
                        The QR code will encode the text you enter below. The other side will display regular engraved text.
                      </p>
                    </div>
                  )}

                  {/* QR Code Text Input - only for database orders */}
                  {resolvedOrderType === 'database' && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-3">
                        Text to Encode in QR Code
                        <span className="text-xs font-normal text-light-textMuted dark:text-dark-textMuted ml-2">
                          (This text will be encoded in the QR code, not displayed as text)
                        </span>
                      </label>
                      <div className="space-y-3">
                        <LineInputWithFormat
                          label={<>Line 1 <span className="text-red-500">*</span></>}
                          value={qrCodeText.line1}
                          onChange={(value) => handleQrCodeTextChange("line1", value)}
                          format={getLineFormat(qrCodeText, "line1")}
                          onFormatChange={(format) => handleQrCodeLineFormatChange("line1", format)}
                          placeholder="e.g., Pet Name: Max"
                          maxLength={50}
                          required
                          inputClassName="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all"
                        />
                        <LineInputWithFormat
                          label="Line 2 (Optional)"
                          value={qrCodeText.line2}
                          onChange={(value) => handleQrCodeTextChange("line2", value)}
                          format={getLineFormat(qrCodeText, "line2")}
                          onFormatChange={(format) => handleQrCodeLineFormatChange("line2", format)}
                          placeholder="e.g., Phone or address"
                          maxLength={50}
                          inputClassName="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all"
                        />
                        <LineInputWithFormat
                          label="Line 3 (Optional)"
                          value={qrCodeText.line3}
                          onChange={(value) => handleQrCodeTextChange("line3", value)}
                          format={getLineFormat(qrCodeText, "line3")}
                          onFormatChange={(format) => handleQrCodeLineFormatChange("line3", format)}
                          placeholder="e.g., Owner name"
                          maxLength={50}
                          inputClassName="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all"
                        />
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                        When scanned, the QR code will display these three lines of text.
                      </p>
                    </div>
                  )}

                  {/* Font Selection - applies to both sides */}
                  <div>
                    <FontDropdown
                      selectedFont={selectedFont}
                      onFontChange={handleFontChange}
                      previewText={side1Config.line1 || side2Config.line1 || "Sample Text"}
                    />
                  </div>

                  {/* Side Indicator and Flip Button */}
                  <div className="flex items-center justify-between p-4 bg-light-surface dark:bg-dark-surfaceLight rounded-lg border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold transition-all ${
                          designingSide === 1 
                            ? 'bg-btn-primary dark:bg-btn-primary-dark text-white scale-110' 
                            : 'bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted'
                        }`}>
                          1
                        </div>
                        <span className={`text-sm font-medium ${
                          designingSide === 1 
                            ? 'text-light-primary dark:text-dark-primary' 
                            : 'text-light-textMuted dark:text-dark-textMuted'
                        }`}>
                          Side 1 {side1Config.line1 || side1Config.line2 || side1Config.line3 ? '✓' : ''}
                        </span>
                      </div>
                      <div className="text-light-textMuted dark:text-dark-textMuted">|</div>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold transition-all ${
                          designingSide === 2 
                            ? 'bg-btn-primary dark:bg-btn-primary-dark text-white scale-110' 
                            : 'bg-light-surface dark:bg-dark-surface text-light-textMuted dark:text-dark-textMuted'
                        }`}>
                          2
                        </div>
                        <span className={`text-sm font-medium ${
                          designingSide === 2 
                            ? 'text-light-primary dark:text-dark-primary' 
                            : 'text-light-textMuted dark:text-dark-textMuted'
                        }`}>
                          Side 2 {side2Config.line1 || side2Config.line2 || side2Config.line3 ? '✓' : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFlipDesignSide();
                      }}
                      className="flex items-center gap-2 text-sm font-semibold text-light-primary dark:text-dark-primary hover:text-light-primaryHover dark:hover:text-dark-primaryHover transition-colors px-4 py-2 rounded-lg hover:bg-light-surface dark:hover:bg-dark-surfaceHover border border-light-border dark:border-dark-border"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Flip to Side {designingSide === 1 ? 2 : 1}</span>
                    </button>
                  </div>

                  {/* Side 1 Inputs - only show if not QR code side for database orders */}
                  {designingSide === 1 && !(resolvedOrderType === "database" && qrCodeSide === 1) && (
                    <div className="space-y-3 transition-opacity duration-300">
                      <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-2">
                        Side 1 Text (Front)
                      </label>
                      <LineInputWithFormat
                        label={<>Line 1 <span className="text-red-500">*</span></>}
                        value={side1Config.line1}
                        onChange={(value) => handleTagTextChange(1, "line1", value)}
                        format={getLineFormat(side1Config, "line1")}
                        onFormatChange={(format) => handleTagLineFormatChange(1, "line1", format)}
                        placeholder="e.g., Pet Name"
                        maxLength={25}
                        required
                      />
                      <LineInputWithFormat
                        label="Line 2 (Optional)"
                        value={side1Config.line2}
                        onChange={(value) => handleTagTextChange(1, "line2", value)}
                        format={getLineFormat(side1Config, "line2")}
                        onFormatChange={(format) => handleTagLineFormatChange(1, "line2", format)}
                        placeholder="e.g., Phone number"
                        maxLength={25}
                      />
                      <LineInputWithFormat
                        label="Line 3 (Optional)"
                        value={side1Config.line3}
                        onChange={(value) => handleTagTextChange(1, "line3", value)}
                        format={getLineFormat(side1Config, "line3")}
                        onFormatChange={(format) => handleTagLineFormatChange(1, "line3", format)}
                        placeholder="e.g., More details"
                        maxLength={25}
                      />
                    </div>
                  )}

                  {/* Side 2 Inputs - only show if not QR code side for database orders */}
                  {designingSide === 2 && !(resolvedOrderType === "database" && qrCodeSide === 2) && (
                    <div className="space-y-3 transition-opacity duration-300">
                      <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-2">
                        Side 2 Text (Back)
                      </label>
                      <LineInputWithFormat
                        label={<>Line 1 <span className="text-red-500">*</span></>}
                        value={side2Config.line1}
                        onChange={(value) => handleTagTextChange(2, "line1", value)}
                        format={getLineFormat(side2Config, "line1")}
                        onFormatChange={(format) => handleTagLineFormatChange(2, "line1", format)}
                        placeholder="e.g., Contact info"
                        maxLength={25}
                        required
                      />
                      <LineInputWithFormat
                        label="Line 2 (Optional)"
                        value={side2Config.line2}
                        onChange={(value) => handleTagTextChange(2, "line2", value)}
                        format={getLineFormat(side2Config, "line2")}
                        onFormatChange={(format) => handleTagLineFormatChange(2, "line2", format)}
                        placeholder="e.g., Phone number"
                        maxLength={25}
                      />
                      <LineInputWithFormat
                        label="Line 3 (Optional)"
                        value={side2Config.line3}
                        onChange={(value) => handleTagTextChange(2, "line3", value)}
                        format={getLineFormat(side2Config, "line3")}
                        onFormatChange={(format) => handleTagLineFormatChange(2, "line3", format)}
                        placeholder="e.g., More details"
                        maxLength={25}
                      />
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 p-3 rounded">
                      <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Progress indicator */}
                  <div className="pt-3 border-t border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between text-xs text-light-textMuted dark:text-dark-textMuted mb-3">
                      <span>Design Progress:</span>
                      <span className="font-semibold text-light-text dark:text-dark-text">
                        {[
                          side1Config.line1 || side1Config.line2 || side1Config.line3 ? 'Side 1' : null,
                          side2Config.line1 || side2Config.line2 || side2Config.line3 ? 'Side 2' : null
                        ].filter(Boolean).join(' & ') || 'Not started'}
                      </span>
                    </div>
                    <div className="w-full bg-light-surface dark:bg-dark-surfaceLight rounded-full h-2">
                      <div 
                        className="bg-btn-primary dark:bg-btn-primary-dark h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${[
                            side1Config.line1 || side1Config.line2 || side1Config.line3,
                            side2Config.line1 || side2Config.line2 || side2Config.line3
                          ].filter(Boolean).length * 50}%` 
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set("step", "3");
                        setSearchParams(newParams);
                        setStep(3);
                      }}
                      className="flex-1 px-5 py-2.5 text-sm border-2 border-light-border dark:border-dark-border rounded-lg font-semibold text-light-text dark:text-dark-text bg-white dark:bg-dark-surface hover:bg-light-surface dark:hover:bg-dark-surfaceHover transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isSubmitting || 
                        (resolvedOrderType === 'database' 
                          ? (!qrCodeText.line1 && !qrCodeText.line2 && !qrCodeText.line3) ||
                            (qrCodeSide === 1 ? !(side2Config.line1 || side2Config.line2 || side2Config.line3) : !(side1Config.line1 || side1Config.line2 || side1Config.line3))
                          : !(side1Config.line1 || side1Config.line2 || side1Config.line3) || !(side2Config.line1 || side2Config.line2 || side2Config.line3)
                        )
                      }
                      className="flex-1 px-5 py-2.5 text-sm bg-btn-primary dark:bg-btn-primary-dark text-white rounded-lg font-bold hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {isSubmitting ? "Submitting..." : "Continue to Decorations"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        )}

        {/* Step 5: Accessory Selection */}
        {step === 5 && selectedMaterial && resolvedOrderType && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-light-text dark:text-dark-text mb-1">Add Decorations</h2>
              <p className="text-sm text-light-textMuted dark:text-dark-textMuted">
                Optional — choose a decoration for each side. They'll be engraved around your text.
              </p>
            </div>

            {renderSelectedBranchBanner()}

            <div className="grid lg:grid-cols-[minmax(0,35fr)_minmax(300px,65fr)] gap-4 lg:gap-5 items-stretch">
              {/* Tag preview — same treatment as the Design step's left column */}
              <div className="lg:sticky lg:top-4 order-1 min-w-0 flex flex-col min-h-[280px] rounded-xl border border-light-border dark:border-dark-border bg-light-surface/40 dark:bg-dark-surfaceLight/25 p-3 sm:p-4">
                <TagPreview
                  embedded
                  fillContainer
                  previewScale={1.4}
                  className="flex-1"
                  material={selectedMaterial}
                  orderType={resolvedOrderType}
                  formData={resolvedOrderType === "database" ? qrForm : engraveForm}
                  side1Config={side1Config}
                  side2Config={side2Config}
                  qrCodeSide={qrCodeSide}
                  qrCodeText={qrCodeText}
                  selectedFont={selectedFont}
                  textCase={textCase}
                  currentSide={accessoryDesigningSide}
                  accessories={[
                    ...(side1Accessory ? [{ side: 1, type: side1Accessory.id, zone: side1Accessory.zone }] : []),
                    ...(side2Accessory ? [{ side: 2, type: side2Accessory.id, zone: side2Accessory.zone }] : []),
                  ]}
                />
              </div>

              {/* Decoration controls */}
              <div className="space-y-4 order-2 min-w-0">
                {/* Side toggle for the picker */}
                <div className="flex gap-2">
                  {[1, 2].map((s) => {
                    const sideConfig = s === 1 ? side1Config : side2Config;
                    const sideLineCount = [sideConfig.line1, sideConfig.line2, sideConfig.line3].filter(Boolean).length;
                    const sideIsFull = sideLineCount >= 3;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setAccessoryDesigningSide(s)}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                          accessoryDesigningSide === s
                            ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                            : "border-light-border dark:border-dark-border text-light-textMuted dark:text-dark-textMuted hover:border-amber-400"
                        }`}
                      >
                        Side {s}{" "}
                        {s === 1 && side1Accessory ? "✓" : ""}
                        {s === 2 && side2Accessory ? "✓" : ""}
                        {sideIsFull && (
                          <span className="text-[11px] font-normal text-light-textMuted dark:text-dark-textMuted"> · Full</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <AccessoryPicker
                  side1Accessory={side1Accessory}
                  side2Accessory={side2Accessory}
                  side1Config={side1Config}
                  side2Config={side2Config}
                  designingSide={accessoryDesigningSide}
                  onChange={handleAccessoryChange}
                />

                <div className="flex gap-3 pt-2 border-t border-light-border dark:border-dark-border">
                  <button
                    type="button"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set("step", "4");
                      setSearchParams(newParams);
                      setStep(4);
                    }}
                    className="flex-1 px-5 py-2.5 text-sm border-2 border-light-border dark:border-dark-border rounded-lg font-semibold text-light-text dark:text-dark-text bg-white dark:bg-dark-surface hover:bg-light-surface dark:hover:bg-dark-surfaceHover transition-all"
                  >
                    Back to Design
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set("step", "6");
                      setSearchParams(newParams);
                      setStep(6);
                    }}
                    className="flex-1 px-5 py-2.5 text-sm bg-btn-primary dark:bg-btn-primary-dark text-white rounded-lg font-bold hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    {side1Accessory || side2Accessory ? "Continue to Review" : "Skip & Continue"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Review & Confirm */}
        {step === 6 && resolvedOrderType && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-card dark:shadow-card-dark p-6 border-2 border-light-border dark:border-dark-border">
            <h2 className="text-2xl font-bold mb-4 text-center text-light-text dark:text-dark-text">Review Your Design</h2>
            <p className="text-sm text-light-textMuted dark:text-dark-textMuted mb-4 text-center">
              Please review your tag design and information before proceeding to payment
            </p>

            {renderSelectedBranchBanner()}

            <div className="mb-6">
              <TagPreview
                material={selectedMaterial}
                orderType={resolvedOrderType}
                formData={resolvedOrderType === "database" ? qrForm : engraveForm}
                qrCodePosition={qrCodePosition}
                onQrPositionChange={setQrCodePosition}
                side1Config={side1Config}
                side2Config={side2Config}
                qrCodeSide={qrCodeSide}
                qrCodeText={qrCodeText}
                selectedFont={selectedFont}
                textCase={textCase}
                onTextCaseChange={setTextCase}
                accessories={[
                  ...(side1Accessory ? [{ side: 1, type: side1Accessory.id, zone: side1Accessory.zone }] : []),
                  ...(side2Accessory ? [{ side: 2, type: side2Accessory.id, zone: side2Accessory.zone }] : []),
                ]}
              />
            </div>

            <div className="border-t border-light-border dark:border-dark-border pt-4 space-y-3">
              <h3 className="text-lg font-bold mb-4 text-light-text dark:text-dark-text">Order Summary</h3>

              <div className="bg-light-surface dark:bg-dark-surfaceLight border border-light-border dark:border-dark-border rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Tag Shape:</span>
                  <span className="font-bold text-light-text dark:text-dark-text">{selectedMaterial?.shapeName} - {selectedMaterial?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Order Type:</span>
                  <span className="font-bold text-light-text dark:text-dark-text">
                    {resolvedOrderType === "database" ? "QR Code Tag" : "Text-Only Tag"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Side 1 Text:</span>
                  <span className="font-bold text-light-text dark:text-dark-text text-right">
                    {[side1Config.line1, side1Config.line2, side1Config.line3].filter(Boolean).join(' • ') || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Side 2 Text:</span>
                  <span className="font-bold text-light-text dark:text-dark-text text-right">
                    {[side2Config.line1, side2Config.line2, side2Config.line3].filter(Boolean).join(' • ') || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-textMuted dark:text-dark-textMuted font-medium">Font Style:</span>
                  <span className="font-bold text-light-text dark:text-dark-text">
                    {selectedFont ? AVAILABLE_FONTS.find(f => f.id === selectedFont)?.name || selectedFont : 'Default'}
                  </span>
                </div>
                <div className="flex justify-between text-lg pt-3 border-t border-light-border dark:border-dark-border">
                  <span className="text-light-text dark:text-dark-text font-semibold">Subtotal:</span>
                  <span className="font-bold text-light-primary dark:text-dark-primary">
                    ${selectedMaterial?.price}
                  </span>
                </div>
              </div>
            </div>

            {/* Checkout Form - Contact Info & Address */}
            <div className="border-t border-light-border dark:border-dark-border pt-4 mt-4">
              <h3 className="text-lg font-bold mb-4 text-light-text dark:text-dark-text">Checkout Information</h3>
              <p className="text-sm text-light-textMuted dark:text-dark-textMuted mb-4">
                Please provide your contact information and shipping address
              </p>
              
              <form onSubmit={(e) => { e.preventDefault(); handleConfirmOrder(); }} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstname"
                      value={resolvedOrderType === "database" ? qrForm.firstname : engraveForm.name?.split(" ")[0] || ""}
                      onChange={resolvedOrderType === "database" ? handleQrFormChange : (e) => {
                        const firstname = e.target.value;
                        const lastname = engraveForm.name?.split(" ").slice(1).join(" ") || "";
                        setEngraveForm(prev => ({ ...prev, name: [firstname, lastname].filter(Boolean).join(" ") }));
                      }}
                      placeholder="John"
                      className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastname"
                      value={resolvedOrderType === "database" ? qrForm.lastname : engraveForm.name?.split(" ").slice(1).join(" ") || ""}
                      onChange={resolvedOrderType === "database" ? handleQrFormChange : (e) => {
                        const firstname = engraveForm.name?.split(" ")[0] || "";
                        const lastname = e.target.value;
                        setEngraveForm(prev => ({ ...prev, name: [firstname, lastname].filter(Boolean).join(" ") }));
                      }}
                      placeholder="Doe"
                      className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={resolvedOrderType === "database" ? qrForm.email : engraveForm.email}
                      onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={resolvedOrderType === "database" ? qrForm.phone : engraveForm.phone}
                      onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                      className={`w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all text-light-text dark:text-dark-text ${
                        (resolvedOrderType === "database" ? phoneErrors.qr : phoneErrors.engrave) ? 'border-red-500' : 'border-light-border dark:border-dark-border'
                      }`}
                      required
                    />
                    {(resolvedOrderType === "database" ? phoneErrors.qr : phoneErrors.engrave) && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{resolvedOrderType === "database" ? phoneErrors.qr : phoneErrors.engrave}</p>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-light-border dark:border-dark-border">
                  <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-3">
                    Shipping Address
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="address_line_1"
                        value={resolvedOrderType === "database" ? qrForm.address_line_1 : engraveForm.address_line_1}
                        onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                        placeholder="123 Main St"
                        className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                        Apt/Unit
                      </label>
                      <input
                        type="text"
                        name="address_line_2"
                        value={resolvedOrderType === "database" ? qrForm.address_line_2 : engraveForm.address_line_2}
                        onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                        placeholder="Apt 4B"
                        className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={resolvedOrderType === "database" ? qrForm.city : engraveForm.city}
                          onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                          placeholder="Salt Lake City"
                          className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={resolvedOrderType === "database" ? qrForm.state : engraveForm.state}
                          onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                          placeholder="UT"
                          maxLength={2}
                          className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all uppercase"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                        ZIP Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="zip"
                        value={resolvedOrderType === "database" ? qrForm.zip : engraveForm.zip}
                        onChange={resolvedOrderType === "database" ? handleQrFormChange : handleEngraveChange}
                        placeholder="84118"
                        maxLength={10}
                        className="w-full px-4 py-2.5 text-sm bg-white dark:bg-dark-surfaceLight border-2 border-light-border dark:border-dark-border text-light-text dark:text-dark-text rounded-lg focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary focus:border-light-primary dark:focus:border-dark-primary transition-all"
                        required
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded mt-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-6 mt-6 border-t border-light-border dark:border-dark-border">
              <button
                type="button"
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set("step", "5");
                  setSearchParams(newParams);
                  setStep(5);
                }}
                className="flex-1 px-5 py-2.5 text-sm border-2 border-light-border dark:border-dark-border rounded-lg font-semibold text-light-text dark:text-dark-text bg-white dark:bg-dark-surface hover:bg-light-surface dark:hover:bg-dark-surfaceHover transition-all"
              >
                Back to Decorations
              </button>
              <button
                onClick={handleConfirmOrder}
                disabled={isSubmitting}
                className="flex-1 px-5 py-2.5 text-sm bg-btn-primary dark:bg-btn-primary-dark text-white rounded-lg font-bold hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? "Processing..." : "Confirm & Proceed to Payment"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaterialSelection;